import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../config';
import LoadingSpinner from './LoadingSpinner';

const EscrowComponent = () => {
  const [escrows, setEscrows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedEscrow, setSelectedEscrow] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [form, setForm] = useState({
    seller_email: '',
    amount: '',
    currency: 'CUALCOIN',
    item_description: '',
    terms: '',
    expires_hours: 72
  });

  useEffect(() => {
    fetchEscrows();
  }, []);

  const fetchEscrows = async () => {
    try {
      const response = await axios.get(`${API}/escrow/active`);
      setEscrows(response.data.escrows);
    } catch (error) {
      console.error('Failed to fetch escrows:', error);
    } finally {
      setLoading(false);
    }
  };

  const createEscrow = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      await axios.post(`${API}/escrow/hold`, {
        ...form,
        amount: parseFloat(form.amount),
        expires_hours: parseInt(form.expires_hours)
      });
      alert('¡Escrow creado! Los fondos han sido retenidos.');
      setShowCreateForm(false);
      setForm({ seller_email: '', amount: '', currency: 'CUALCOIN', item_description: '', terms: '', expires_hours: 72 });
      fetchEscrows();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al crear escrow');
    } finally {
      setProcessing(false);
    }
  };

  const releaseEscrow = async (escrowId) => {
    if (!window.confirm('¿Confirmas la liberación de fondos al vendedor? Esta acción no se puede deshacer.')) return;
    setProcessing(true);
    try {
      await axios.post(`${API}/escrow/release`, { escrow_id: escrowId });
      alert('¡Fondos liberados al vendedor!');
      fetchEscrows();
      setSelectedEscrow(null);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al liberar escrow');
    } finally {
      setProcessing(false);
    }
  };

  const refundEscrow = async (escrowId) => {
    const reason = window.prompt('Razón de la devolución:');
    if (!reason) return;
    setProcessing(true);
    try {
      await axios.post(`${API}/escrow/refund`, { escrow_id: escrowId, reason });
      alert('¡Fondos devueltos al comprador!');
      fetchEscrows();
      setSelectedEscrow(null);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al devolver escrow');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Comercio P2P - Escrow</h2>
          <p className="text-gray-600">Sistema de fondos retenidos para transacciones seguras</p>
        </div>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          data-testid="create-escrow-btn"
          className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all"
        >
          {showCreateForm ? 'Cancelar' : '+ Nuevo Escrow'}
        </button>
      </div>

      {/* Explicación */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-700 mb-2">¿Cómo funciona el Escrow?</h3>
        <ol className="text-xs text-blue-600 space-y-1 list-decimal list-inside">
          <li><strong>Comprador crea escrow:</strong> Los fondos se retienen de su wallet</li>
          <li><strong>Vendedor entrega:</strong> El producto o servicio acordado</li>
          <li><strong>Comprador confirma:</strong> Libera los fondos al vendedor</li>
          <li><strong>Disputa:</strong> El vendedor puede devolver si hay problemas</li>
        </ol>
      </div>

      {/* Formulario de creación */}
      {showCreateForm && (
        <div className="bg-white rounded-xl shadow-lg p-6 border border-purple-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Crear Nuevo Escrow</h3>
          <form onSubmit={createEscrow} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Email del Vendedor</label>
                <input
                  type="email"
                  required
                  value={form.seller_email}
                  onChange={(e) => setForm({...form, seller_email: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                  placeholder="vendedor@email.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Moneda</label>
                <select
                  value={form.currency}
                  onChange={(e) => setForm({...form, currency: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="CUALCOIN">CUALcoin</option>
                  <option value="PUNTOS_VERDES">CUALverde (CV)</option>
                  <option value="CUAL-USD">CUAL-USD</option>
                  <option value="CUAL-EUR">CUAL-EUR</option>
                </select>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Monto</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={form.amount}
                  onChange={(e) => setForm({...form, amount: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                  placeholder="100.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Expira en (horas)</label>
                <select
                  value={form.expires_hours}
                  onChange={(e) => setForm({...form, expires_hours: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="24">24 horas</option>
                  <option value="48">48 horas</option>
                  <option value="72">72 horas (recomendado)</option>
                  <option value="168">1 semana</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Descripción del Item/Servicio</label>
              <input
                type="text"
                value={form.item_description}
                onChange={(e) => setForm({...form, item_description: e.target.value})}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                placeholder="Ej: iPhone 13 Pro usado en buen estado"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Términos y Condiciones</label>
              <textarea
                value={form.terms}
                onChange={(e) => setForm({...form, terms: e.target.value})}
                rows={2}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                placeholder="Ej: Entrega en 3 días hábiles, incluye caja original..."
              />
            </div>

            <button
              type="submit"
              disabled={processing}
              data-testid="submit-escrow-btn"
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-2 px-4 rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 transition-all"
            >
              {processing ? 'Procesando...' : 'Crear Escrow y Retener Fondos'}
            </button>
          </form>
        </div>
      )}

      {/* Lista de Escrows */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50">
          <h3 className="text-lg font-semibold text-gray-900">Mis Escrows Activos</h3>
        </div>
        
        {escrows.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <span className="text-4xl">🤝</span>
            <p className="mt-4 text-gray-500">No tienes escrows activos</p>
            <p className="text-sm text-gray-400">Crea uno para realizar una transacción P2P segura</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {escrows.map((escrow) => (
              <div key={escrow.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        escrow.role === 'buyer' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {escrow.role === 'buyer' ? 'Comprador' : 'Vendedor'}
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        escrow.state === 'held' 
                          ? 'bg-orange-100 text-orange-800' 
                          : escrow.state === 'released'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                      }`}>
                        {escrow.state === 'held' ? '🔒 Retenido' : escrow.state === 'released' ? '✅ Liberado' : escrow.state}
                      </span>
                    </div>
                    <p className="mt-2 text-lg font-semibold text-gray-900">
                      {escrow.amount} {escrow.currency}
                    </p>
                    <p className="text-sm text-gray-600">
                      {escrow.role === 'buyer' ? 'Vendedor' : 'Comprador'}: {escrow.role === 'buyer' ? escrow.seller_email : escrow.buyer_email}
                    </p>
                    {escrow.item_description && (
                      <p className="text-sm text-gray-500 mt-1">"{escrow.item_description}"</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      Expira: {new Date(escrow.expires_at).toLocaleString('es-ES')}
                    </p>
                  </div>
                  
                  <div className="flex space-x-2">
                    {escrow.state === 'held' && escrow.role === 'buyer' && (
                      <button
                        onClick={() => releaseEscrow(escrow.id)}
                        disabled={processing}
                        data-testid={`release-escrow-${escrow.id}`}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm"
                      >
                        Liberar Fondos
                      </button>
                    )}
                    {escrow.state === 'held' && escrow.role === 'seller' && (
                      <button
                        onClick={() => refundEscrow(escrow.id)}
                        disabled={processing}
                        data-testid={`refund-escrow-${escrow.id}`}
                        className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 disabled:opacity-50 text-sm"
                      >
                        Devolver
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Admin Panel Component


export default EscrowComponent;
